import React from 'react';

interface FormButtonGroupProps {
  label: string;
  required?: boolean;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

function getFormButtonEdgeBorder(
  isEdge: boolean,
  isSelected: boolean,
  selectedBorder: string,
  defaultBorder: string
): string {
  if (isEdge) {
    return isSelected ? selectedBorder : 'none';
  }
  return isSelected ? selectedBorder : defaultBorder;
}

const FormButtonGroup: React.FC<FormButtonGroupProps> = ({
  label,
  required = false,
  options,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* Button Group */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          borderRadius: '6px',
          overflow: 'hidden',
          border: `1px solid ${error ? '#dc2626' : 'var(--border-input-default)'}`,
        }}
      >
        {options.map((option, index) => {
          const isSelected = value === option.value;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;
          const selectedBorder = '1px solid #3b82f6';
          const defaultBorder = '1px solid var(--border-input-default)';
          const edgeBorder = (isEdge: boolean) =>
            getFormButtonEdgeBorder(isEdge, isSelected, selectedBorder, defaultBorder);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              style={{
                flex: 1,
                height: '36px',
                padding: '0 12px',
                backgroundColor: isSelected ? '#1e3a8a' : '#0f172a',
                border: isSelected ? selectedBorder : 'none',
                borderRight: edgeBorder(isLast),
                borderTop: edgeBorder(isFirst),
                borderBottom: isSelected ? selectedBorder : 'none',
                borderRadius: isFirst ? '6px 0 0 6px' : isLast ? '0 6px 6px 0' : '0',
                fontSize: '14px',
                fontWeight: 500,
                color: '#ffffff',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={e => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = '#1e293b';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = isSelected ? '#1e3a8a' : '#0f172a';
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FormButtonGroup;
